import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE, absoluteUrl, getRouteSeo } from "./siteConfig";

const upsertMeta = (key, value, attribute = "name") => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!value) { element?.remove(); return; }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", value);
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = href;
};

const safeJson = (value) => JSON.stringify(value).replace(/</g, "\\u003c");

const breadcrumbSchema = (page) => {
  if (!page.breadcrumb || page.path === "/") return null;
  const items = [{ "@type": "ListItem", position: 1, name: "Home", item: `${SITE.origin}/` }];
  if (page.path.startsWith("/services/")) {
    items.push({ "@type": "ListItem", position: 2, name: "Services", item: `${SITE.origin}/#services` });
  }
  items.push({ "@type": "ListItem", position: items.length + 1, name: page.breadcrumb, item: absoluteUrl(page.path) });
  return { "@type": "BreadcrumbList", "@id": `${absoluteUrl(page.path)}#breadcrumb`, itemListElement: items };
};

const globalSchema = [
  {
    "@type": ["Organization", "LocalBusiness"],
    "@id": SITE.organizationId,
    name: SITE.name,
    url: `${SITE.origin}/`,
    logo: absoluteUrl("/logoprime.avif"),
    image: absoluteUrl(SITE.defaultImage),
    email: SITE.email,
    telephone: SITE.phone,
    areaServed: { "@type": "AdministrativeArea", name: SITE.areaServed },
    address: { "@type": "PostalAddress", addressLocality: "Maple Valley", addressRegion: "WA", addressCountry: "US" },
  },
  {
    "@type": "WebSite",
    "@id": SITE.websiteId,
    url: `${SITE.origin}/`,
    name: SITE.name,
    inLanguage: SITE.language,
    publisher: { "@id": SITE.organizationId },
  },
];

const buildSchema = (page) => {
  const url = absoluteUrl(page.path);
  const graph = [...globalSchema, {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    inLanguage: SITE.language,
    isPartOf: { "@id": SITE.websiteId },
    about: { "@id": SITE.organizationId },
  }];
  if (page.serviceName) graph.push({
    "@type": "Service",
    "@id": `${url}#service`,
    name: page.serviceName,
    description: page.description,
    url,
    provider: { "@id": SITE.organizationId },
    areaServed: SITE.areaServed,
  });
  const breadcrumb = breadcrumbSchema(page);
  if (breadcrumb) graph.push(breadcrumb);
  if (page.article) graph.push(page.article);
  return { "@context": "https://schema.org", "@graph": graph };
};

export const applySeo = (page) => {
  const isProductionHost = window.location.hostname === "www.primedlandscaping.com";
  const canonical = absoluteUrl(page.path);
  const image = absoluteUrl(page.image || SITE.defaultImage);
  const noIndex = page.noIndex || !isProductionHost;
  document.documentElement.lang = SITE.language;
  document.title = page.title;
  upsertMeta("description", page.description);
  upsertMeta("robots", noIndex ? "noindex, nofollow, noarchive" : "index, follow, max-image-preview:large");
  upsertCanonical(canonical);
  upsertMeta("og:site_name", SITE.name, "property");
  upsertMeta("og:type", page.article ? "article" : "website", "property");
  upsertMeta("og:title", page.title, "property");
  upsertMeta("og:description", page.description, "property");
  upsertMeta("og:url", canonical, "property");
  upsertMeta("og:image", image, "property");
  upsertMeta("og:image:alt", page.imageAlt || page.title, "property");
  upsertMeta("og:locale", "en_US", "property");
  upsertMeta("twitter:card", "summary_large_image");
  upsertMeta("twitter:title", page.title);
  upsertMeta("twitter:description", page.description);
  upsertMeta("twitter:image", image);
  upsertMeta("twitter:image:alt", page.imageAlt || page.title);
  const verification = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION;
  upsertMeta("google-site-verification", verification || "");
  let script = document.head.querySelector('script[data-seo-schema="true"]');
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seoSchema = "true";
    document.head.appendChild(script);
  }
  script.textContent = safeJson(buildSchema(page));
};

const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = getRouteSeo(pathname);
    if (page) applySeo(page);
    else if (pathname.startsWith("/blogs/")) applySeo({ path: pathname, title: `Loading article | ${SITE.name}`, description: "Landscaping advice from Primed Landscaping.", noIndex: true });
    else applySeo({ path: pathname, title: `Page Not Found | ${SITE.name}`, description: "The requested page could not be found.", noIndex: true });
  }, [pathname]);
  return null;
};

export default RouteSeo;
